import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { FilterByForm, FilterField, FilterOption } from 'models/Filters';
import { Button } from 'ncoded-component-library';
import { FieldArray } from 'react-final-form-arrays';
import { Field, useFormState } from 'react-final-form';
import SelectField from 'components/SelectField';
import TrashIcon from 'icons/Trash.icon';
import ResolveMultiSelectField from 'components/ResolveMultiSelectField';
import PlusIcon from 'icons/Plus.icon';
import useMatchMedia from 'hooks/useMatchMedia';
import { isSingleElementArray, sortOptions } from 'utils';
import ChevrondownIcon from 'icons/Chevrondown.icon';

import './MultiFilterOptions.styles.scss';

export const INITIAL_VALUE = {
  name: '',
  value: '',
} as FilterField;

export type MultiFilterOptionsProps = {
  className?: string;
  filters: FilterOption[];
};

const MultiFilterOptions: React.FC<MultiFilterOptionsProps> = (props) => {
  const { className, filters } = props;

  const classes = classNames(
    'multi-filter-options',
    'multi-fields-options',
    className,
  );

  const { t } = useTranslation();
  const isPhablet = useMatchMedia('isPhablet');

  const {
    values: { filters: formFilters },
  } = useFormState<FilterByForm>();

  return (
    <div className={classes}>
      <FieldArray name="filters">
        {({ fields }) => (
          <>
            <div>
              {fields.map((name, index) => {
                const isFirstElement = index === 0;

                const { name: filterName, value: filterValue } =
                  fields.value[index] || {};

                const selectedFilter = filters.find(
                  ({ value }) => value === filterName,
                );

                const {
                  isSearchable,
                  shouldSortOptions = true,
                  options = [],
                  render,
                } = selectedFilter || {};

                const selectedFilterOptions = shouldSortOptions
                  ? sortOptions(options)
                  : options;

                return (
                  <div key={name} className="row">
                    <div>
                      <p>{t(isFirstElement ? 'filterBy' : 'thenBy')}</p>

                      <Field
                        name={`${name}.name`}
                        component={SelectField}
                        renderAsPortal={!isPhablet}
                        portalClassName="multi-filter-select"
                        icon={<ChevrondownIcon />}
                        customOnChange={(name: string) =>
                          fields.update(index, { name, value: [] })
                        }
                        options={filters.map(({ value, label }) => ({
                          value,
                          label,
                          disabled: formFilters.find(
                            ({ name }) => name === value,
                          ),
                        }))}
                      />
                    </div>

                    <div>
                      {filterName &&
                        (render ? (
                          render({
                            value: filterValue,
                            onChange: (value) =>
                              fields.update(index, { name: filterName, value }),
                          })
                        ) : selectedFilter?.isMultiple ? (
                          <Field
                            name={`${name}.value`}
                            component={ResolveMultiSelectField}
                            renderAsPortal={!isPhablet}
                            hasCheckbox
                            hasCheckboxAll
                            searchable={isSearchable}
                            placeholder={t('selectAnOption')}
                            portalClassName="multi-filter-multi-select"
                            options={selectedFilterOptions}
                          />
                        ) : (
                          <Field
                            name={`${name}.value`}
                            component={SelectField}
                            searchable={isSearchable}
                            icon={<ChevrondownIcon />}
                            renderAsPortal={!isPhablet}
                            portalClassName="multi-filter-select"
                            options={selectedFilterOptions}
                          />
                        ))}

                      {!isSingleElementArray(fields?.value) ? (
                        <Button
                          variant="icon"
                          className="warning-action-button"
                          icon={TrashIcon}
                          onClick={() => fields.remove(index)}
                        />
                      ) : (
                        <div className="button-placeholder" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {fields.length < filters.length && (
              <Button
                icon={PlusIcon}
                variant="outline"
                onClick={() => fields.push(INITIAL_VALUE)}
              >
                {t('addFilter')}
              </Button>
            )}
          </>
        )}
      </FieldArray>
    </div>
  );
};

export default MultiFilterOptions;
